import is from '@sindresorhus/is';

import type { JobProfileSkill } from '~/shared/data-contract/skill';

import { useGlobalPermissions } from '../../../app/query-hooks/global-permissions';
import { useCompanySettings } from '../../../company-settings';
import type { CompetencyWithSkills } from '../types';

const UNKNOWN_PROFICIENCY_LEVEL = 0;

type ItemType = 'job-level' | 'job-role';

/** Job roles hide their assigned skill proficiency when the company settings turn it off; job levels always show it. */
export const isSkillProficiencyVisible = ({
    itemType,
    showRoleSkillProficiency,
}: {
    readonly itemType: ItemType;
    readonly showRoleSkillProficiency: boolean | undefined;
}): boolean => itemType === 'job-level' || showRoleSkillProficiency !== false;

/**
 * Roles stay hidden while the company settings, or the permissions that gate them, are loading so a hidden setting
 * never flashes. Settings the user cannot read or that failed to load fall back to showing proficiency.
 */
export const useIsSkillProficiencyVisible = (itemType: ItemType): boolean => {
    const { isPending: isPermissionsPending } = useGlobalPermissions();
    const { data: companySettings, isLoading: isSettingsLoading } = useCompanySettings();

    return isSkillProficiencyVisible({
        itemType,
        showRoleSkillProficiency:
            isPermissionsPending || isSettingsLoading ? false : companySettings?.showRoleSkillProficiency,
    });
};

const withUnknownProficiencyIfUnset = (skill: JobProfileSkill): JobProfileSkill =>
    is.number(skill.level) ? skill : { ...skill, level: UNKNOWN_PROFICIENCY_LEVEL };

/** Skills added while proficiency is hidden cannot be given a level, so they are saved with unknown proficiency. */
export const fillUnsetProficiencyLevels = (competencies: readonly CompetencyWithSkills[]): CompetencyWithSkills[] =>
    competencies.map((competency) => ({ ...competency, skills: competency.skills.map(withUnknownProficiencyIfUnset) }));
